// src/components/ListView.js

import React, { useState, useEffect, useMemo } from 'react';
import { 
    FiSearch, FiPlus, FiChevronLeft, FiChevronRight, 
    FiTrash2, FiChevronUp, FiChevronDown, FiMoreVertical, FiX 
} from 'react-icons/fi';
import { useModal } from '../context/ModalContext';

const ListView = ({
    items = [],
    columns = [],
    onNewItem,
    onDeleteItems,
    onItemClick,
    searchKeys = [],
    filterNode,
    filterFn = null,
    rowClassName = () => '',
    searchPlaceholder = "Search...",
    newItemButtonText = "New Item",
    itemNoun = "item",
    selectedIds = [],
    onSelectOne = () => {},
    onSelectAll = () => {},
    enableSelection = true,
    savedState = {}, 
    onStateChange = () => {} 
}) => {
    // --- STATE ---
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    
    // Spreadsheet Features State
    const [columnWidths, setColumnWidths] = useState(savedState.columnWidths || {});
    
    // Initialize sortConfig as an Array for multi-sort support
    const [sortConfig, setSortConfig] = useState(() => {
        const saved = savedState.sortConfig;
        if (Array.isArray(saved)) return saved;
        if (saved && typeof saved === 'object' && saved.key) return [saved];
        return [];
    });
    
    // Initialize Column Order
    const [orderedColumns, setOrderedColumns] = useState(() => {
        if (savedState.columnOrder && savedState.columnOrder.length > 0) {
            const reordered = [];
            savedState.columnOrder.forEach(accessor => {
                const found = columns.find(c => c.accessor === accessor);
                if (found) reordered.push(found);
            });
            columns.forEach(c => {
                if (!reordered.find(rc => rc.accessor === c.accessor)) {
                    reordered.push(c);
                }
            });
            return reordered;
        }
        return columns;
    });

    const [draggedColumnIndex, setDraggedColumnIndex] = useState(null);
    const [dragOverColumnIndex, setDragOverColumnIndex] = useState(null);

    const { showConfirm } = useModal();

    // Sync columns if the definition changes
    useEffect(() => {
        const currentAccessors = orderedColumns.map(c => c.accessor).sort().join(',');
        const newAccessors = columns.map(c => c.accessor).sort().join(',');
        
        if (currentAccessors !== newAccessors) {
             setOrderedColumns(columns);
        }
    }, [columns]); 

    // Helper: Notify Parent of Changes
    const notifyChange = (updates) => {
        onStateChange({
            columnWidths,
            sortConfig,
            columnOrder: orderedColumns.map(c => c.accessor),
            ...updates
        });
    };

    // --- MULTI-COLUMN SORTING LOGIC ---
    const handleSort = (key, event) => {
        if (event.target.closest('.resizer')) return;

        setSortConfig(prevConfig => {
            const currentSorts = [...prevConfig];
            const existingIndex = currentSorts.findIndex(s => s.key === key);

            if (existingIndex > -1) {
                // Toggle Existing: Asc -> Desc -> Remove
                const currentSort = currentSorts[existingIndex];
                if (currentSort.direction === 'asc') {
                    currentSorts[existingIndex] = { ...currentSort, direction: 'desc' };
                } else {
                    currentSorts.splice(existingIndex, 1);
                }
            } else {
                // Add New (Multi-sort)
                currentSorts.push({ key, direction: 'asc' });
            }

            notifyChange({ sortConfig: currentSorts });
            return currentSorts;
        });
    };

    // --- FILTERING & SORTING PIPELINE ---
    const processedItems = useMemo(() => {
        let result = [...items]; 

        // 1. Search
        if (searchTerm && searchKeys.length > 0) {
            const lowercasedTerm = searchTerm.toLowerCase();
            result = result.filter(item =>
                searchKeys.some(key =>
                    String(item[key] ?? '').toLowerCase().includes(lowercasedTerm)
                )
            );
        }

        // 2. Custom/Prop Filters
        if (Object.keys(filters).length > 0) {
            if (filterFn) {
                result = result.filter(item => filterFn(item, filters));
            } else {
                result = result.filter(item =>
                    Object.entries(filters).every(([key, value]) => {
                        if (!value || value === 'All') return true;
                        if (Array.isArray(item[key])) { return item[key].includes(value); }
                        return item[key] === value;
                    })
                );
            }
        }

        // 3. Sorting Execution (Hierarchical)
        if (sortConfig && sortConfig.length > 0) {
            const compare = (a, b) => {
                for (const sort of sortConfig) {
                    const { key, direction } = sort;
                    let valA = a[key];
                    let valB = b[key];

                    if (valA === null || valA === undefined) valA = '';
                    if (valB === null || valB === undefined) valB = '';

                    if (typeof valA === 'string') valA = valA.toLowerCase();
                    if (typeof valB === 'string') valB = valB.toLowerCase();

                    if (valA < valB) return direction === 'asc' ? -1 : 1;
                    if (valA > valB) return direction === 'asc' ? 1 : -1;
                }
                return 0;
            };

            const hasSubTickets = result.some(i => i.isSubTicket);

            if (hasSubTickets) {
                const roots = [];
                const childrenMap = new Map();

                result.forEach(item => {
                    if (item.isSubTicket) {
                        let pId = item.parentId;
                        if (typeof pId === 'object' && pId !== null) {
                            pId = pId._id || pId.toString(); 
                        }
                        pId = String(pId);
                        if (!childrenMap.has(pId)) childrenMap.set(pId, []);
                        childrenMap.get(pId).push(item);
                    } else {
                        roots.push(item);
                    }
                });

                roots.sort(compare);

                const sortedHierarchical = [];
                roots.forEach(root => {
                    sortedHierarchical.push(root);
                    const rootId = String(root._id);
                    if (childrenMap.has(rootId)) {
                        const kids = childrenMap.get(rootId);
                        kids.sort((a,b) => new Date(a.createdAt) - new Date(b.createdAt));
                        sortedHierarchical.push(...kids);
                        childrenMap.delete(rootId); 
                    }
                });
                
                childrenMap.forEach((kids) => {
                    kids.sort(compare); 
                    sortedHierarchical.push(...kids);
                });

                result = sortedHierarchical;
            } else {
                result.sort(compare);
            }
        }

        return result;
    }, [items, searchTerm, searchKeys, filters, filterFn, sortConfig]);

    // --- PAGINATION & SELECTION ---
    useEffect(() => { setCurrentPage(1); }, [searchTerm, filters, items]);

    const totalPages = Math.ceil(processedItems.length / rowsPerPage);
    const paginatedItems = processedItems.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    const isAllOnPageSelected = paginatedItems.length > 0 && paginatedItems.every(i => selectedIds.includes(i._id));

    const handleSelectAllClick = (e) => {
        const paginatedIds = paginatedItems.map(i => i._id);
        onSelectAll(e.target.checked, paginatedIds);
    };

    const handleDeleteSelected = () => {
        showConfirm(
            `Are you sure you want to delete ${selectedIds.length} selected ${itemNoun}(s)?`,
            () => onDeleteItems(selectedIds),
            `Delete ${itemNoun}s`,
            true
        );
    };

    // --- DRAG & DROP COLUMN HANDLERS ---
    const handleDragStart = (e, index) => {
        setDraggedColumnIndex(index);
        e.dataTransfer.effectAllowed = "move";
    };

    const handleDragOver = (e, index) => {
        e.preventDefault(); 
        if (draggedColumnIndex === null || draggedColumnIndex === index) return;
        setDragOverColumnIndex(index);
    };

    const handleDrop = (e, index) => {
        e.preventDefault();
        if (draggedColumnIndex === null) return;

        const newCols = [...orderedColumns];
        const [movedCol] = newCols.splice(draggedColumnIndex, 1);
        newCols.splice(index, 0, movedCol);

        setOrderedColumns(newCols);
        setDraggedColumnIndex(null);
        setDragOverColumnIndex(null);
        notifyChange({ columnOrder: newCols.map(c => c.accessor) });
    };

    const handleDragEnd = () => {
        setDraggedColumnIndex(null);
        setDragOverColumnIndex(null);
    };

    // --- RESIZING HANDLERS ---
    const handleMouseDownResize = (e, accessor) => {
        e.preventDefault(); e.stopPropagation(); 
        const startX = e.clientX;
        const startWidth = columnWidths[accessor] || 150; 
        let finalWidth = startWidth;

        const handleMouseMove = (moveEvent) => {
            const currentX = moveEvent.clientX;
            const diff = currentX - startX;
            finalWidth = Math.max(50, startWidth + diff); 
            setColumnWidths(prev => ({ ...prev, [accessor]: finalWidth }));
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'default';
            notifyChange({ columnWidths: { ...columnWidths, [accessor]: finalWidth } });
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = 'col-resize';
    };

    return (
        <div className="table-card">
            {/* Header Controls */}
            {enableSelection && selectedIds.length > 0 ? (
                <div className="table-controls bulk-actions-bar">
                    <div className="bulk-actions-info">
                        <span>{selectedIds.length}</span> {itemNoun}{selectedIds.length > 1 ? 's' : ''} selected
                    </div>
                    <div className="bulk-actions-buttons">
                        <button className="btn btn-danger" onClick={handleDeleteSelected}>
                            <FiTrash2 className="btn-icon" /> Delete Selected
                        </button>
                    </div>
                </div>
            ) : (
                <div className="table-controls">
                    <div className="search-input-wrapper">
                        <FiSearch className="search-icon" />
                        <input type="search" className="search-input" placeholder={searchPlaceholder} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    </div>
                    <div className="filters-group">
                        {filterNode && React.cloneElement(filterNode, { onFilterChange: setFilters, filters })}
                        {sortConfig.length > 0 && (
                            <button onClick={() => { setSortConfig([]); notifyChange({ sortConfig: [] }); }} className="btn btn-secondary btn-sm" title="Clear All Sorts">
                                <FiX size={14} style={{marginRight: '4px'}}/> Clear Sorts
                            </button>
                        )}
                    </div>
                    {onNewItem && <button onClick={onNewItem} className="btn btn-primary"><FiPlus className="btn-icon" /> {newItemButtonText}</button>}
                </div>
            )}

            {/* Table Area */}
            <div className="table-wrapper">
                <table style={{ tableLayout: 'fixed', minWidth: '100%' }}>
                    <thead>
                        <tr>
                            {enableSelection && (
                                <th style={{ width: '40px', textAlign: 'center' }}>
                                    <input type="checkbox" className="custom-checkbox" onChange={handleSelectAllClick} checked={isAllOnPageSelected} />
                                </th>
                            )}

                            {orderedColumns.map((col, index) => {
                                const sortState = sortConfig.find(s => s.key === col.accessor);
                                const sortIndex = sortConfig.findIndex(s => s.key === col.accessor);
                                
                                return (
                                <th
                                    key={col.accessor || index}
                                    style={{ 
                                        width: columnWidths[col.accessor] ? `${columnWidths[col.accessor]}px` : 'auto',
                                        position: 'relative'
                                    }}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, index)}
                                    onDragOver={(e) => handleDragOver(e, index)}
                                    onDrop={(e) => handleDrop(e, index)}
                                    onDragEnd={handleDragEnd}
                                    className={`${draggedColumnIndex === index ? 'is-dragging' : ''} ${dragOverColumnIndex === index ? 'drag-over' : ''}`}
                                    onClick={(e) => handleSort(col.accessor, e)}
                                    title="Click to sort. Click again to change direction."
                                >
                                    <div className="draggable-header">
                                        <span className="draggable-header-content">
                                            {col.header}
                                            {sortState && (
                                                <span className={`sort-icon active`} style={{ 
                                                    display: 'inline-flex', 
                                                    alignItems: 'center', 
                                                    gap: '4px',
                                                    color: 'var(--blue-600)' 
                                                }}>
                                                    {sortState.direction === 'asc' ? <FiChevronUp /> : <FiChevronDown />}
                                                    {sortConfig.length > 1 && (
                                                        <span style={{ 
                                                            backgroundColor: 'var(--blue-600)', 
                                                            color: '#fff',
                                                            borderRadius: '50%',
                                                            minWidth: '16px',
                                                            height: '16px',
                                                            fontSize: '10px',
                                                            fontWeight: '700',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            lineHeight: 1,
                                                            boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                                                        }}>
                                                            {sortIndex + 1}
                                                        </span>
                                                    )}
                                                </span>
                                            )}
                                        </span>
                                        
                                        <div 
                                            className="resizer" 
                                            onMouseDown={(e) => handleMouseDownResize(e, col.accessor)}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <FiMoreVertical className="resizer-icon" />
                                        </div>
                                    </div>
                                </th>
                            )})}
                        </tr>
                    </thead>
                    <tbody className="table-animated-rows">
                        {paginatedItems.length > 0 ? paginatedItems.map(item => (
                            <tr key={item._id} className={`clickable-row ${selectedIds.includes(item._id) ? 'selected' : ''} ${rowClassName(item)}`} onClick={() => onItemClick(item._id)}>
                                {enableSelection && (
                                    <td onClick={(e) => e.stopPropagation()} style={{ width: '40px', textAlign: 'center' }}>
                                        <input type="checkbox" className="custom-checkbox" checked={selectedIds.includes(item._id)} onChange={() => onSelectOne(item._id)} />
                                    </td>
                                )}
                                {orderedColumns.map((col, index) => (
                                    <td 
                                        key={col.accessor || index}
                                        style={{ 
                                            width: columnWidths[col.accessor] ? `${columnWidths[col.accessor]}px` : 'auto',
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                        }}
                                        title={typeof item[col.accessor] === 'string' ? item[col.accessor] : ''} 
                                    >
                                        {col.render ? col.render(item) : item[col.accessor]}
                                    </td>
                                ))}
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan={orderedColumns.length + (enableSelection ? 1 : 0)} className="empty-table-row">No {itemNoun}s found.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="table-footer">
                <div className="rows-per-page">
                    <span>Rows per page:</span>
                    <select value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                        <option value={10}>10</option><option value={20}>20</option><option value={50}>50</option>
                    </select>
                </div>
                <div className="pagination-info">
                    <span>{processedItems.length > 0 ? `${(currentPage - 1) * rowsPerPage + 1}-${Math.min(currentPage * rowsPerPage, processedItems.length)} of ${processedItems.length}` : '0 of 0'}</span>
                </div>
                <div className="pagination-controls">
                    <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}><FiChevronLeft /></button>
                    <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage >= totalPages}><FiChevronRight /></button>
                </div>
            </div>
        </div>
    );
};

export default ListView;